/**
 * Core: converts vertical wheel events into horizontal scroll on a given element.
 *
 * Port of scroll-converter.js (Johannes Koggdal, MIT)
 * Extended to support arbitrary elements instead of window-level scrolling.
 */

export interface ScrollConverterOptions {
  /** Multiplier applied to the raw pixel delta. Default: 1 */
  speedMultiplier?: number;
  /** Pixels per "line" when deltaMode is DOM_DELTA_LINE. Default: 20 */
  pixelsPerLine?: number;
  /** Pixels per "page" when deltaMode is DOM_DELTA_PAGE. Default: 300 */
  pixelsPerPage?: number;
}

export interface ScrollConverterInstance {
  /** Remove all event listeners attached to the element. */
  destroy(): void;
}

/**
 * Attaches a wheel listener to `el` that translates vertical scroll input
 * into horizontal scrollLeft changes.
 *
 * When the horizontal component of a wheel event is already dominant (e.g.
 * trackpad horizontal swipe) the event is left untouched so the browser can
 * handle it natively.
 *
 * @returns an object with a `destroy()` method to clean up.
 */
export function applyHorizontalScroll(
  el: HTMLElement,
  options: ScrollConverterOptions = {},
): ScrollConverterInstance {
  const speedMultiplier = options.speedMultiplier ?? 1;
  const pixelsPerLine = options.pixelsPerLine ?? 20;
  const pixelsPerPage = options.pixelsPerPage ?? 300;

  const onWheel = (e: WheelEvent): void => {
    // Skip when the horizontal component already dominates (trackpad swipe, etc.)
    if (Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;

    // Skip if the element is not actually scrollable horizontally
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    if (maxScrollLeft <= 0) return;

    // Normalise the delta to pixels
    let deltaPx: number;
    switch (e.deltaMode) {
      case WheelEvent.DOM_DELTA_LINE:
        deltaPx = e.deltaY * pixelsPerLine;
        break;
      case WheelEvent.DOM_DELTA_PAGE:
        deltaPx = e.deltaY * pixelsPerPage;
        break;
      default:
        // DOM_DELTA_PIXEL
        deltaPx = e.deltaY;
        break;
    }

    const prevScrollLeft = el.scrollLeft;
    el.scrollLeft += deltaPx * speedMultiplier;

    // Only swallow the event if we actually moved (avoids eating overscroll at edges)
    if (el.scrollLeft !== prevScrollLeft) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  el.addEventListener('wheel', onWheel, { passive: false });

  return {
    destroy() {
      el.removeEventListener('wheel', onWheel);
    },
  };
}
